import "../../App.css";
import { LessonWidget } from "../../components/LessonWidget";
import lighting from "../../img/lighting.svg";
import application from "../../img/mainColor.svg";
import profile_icon from "../../img/profile-icon.svg";
import bell_icon from "../../img/bell-icon.svg";
import { Link, useNavigate } from "react-router-dom";
import {
  ILesson,
  ISection,
  IStory,
  IUser,
  IUserScheduled,
  TIME_HOURS_LS,
  TIME_MINUTE_LS,
} from "../../type";
import { HabitDayPadItem } from "../../components/HabitDayPadItem";
import {
  calculateComplitedLesson,
  getActiveScheduleForSection,
  getCurrentScheduled,
  getCurrentSection,
  getLatestStartedSchedule,
  hasGold,
  hasPearl,
  isCompletedLesson,
  prewiusScheduled,
} from "../../utils/current_section";
import { useEffect, useRef, useState } from "react";
import { Modal } from "../../components/Modal";
import { withDrawalSilver } from "../../api/lesson";
import { getNotifications, markAllNotificationsRead, markNotificationRead, TAppNotification } from "../../api/notifications";
import { logClientDiagnostic } from "../../utils/clientDiagnosticLog";
import "./index.css";
import { StoryLineItem } from "../../components/StoryLineItem";

const NOTIFICATIONS_ENABLED = false;

function findLessonById(sections: ISection[], lessonId: number): ILesson | undefined {
  for (const s of sections) {
    const hit = s.Lesson_SectionRelation?.find((l) => l.id === lessonId);
    if (hit) return hit;
  }
  return undefined;
}

type LessonRevertTarget = { lessonId: number; scheduleId: number };

type HabitStripCtx = {
  lessons: ILesson[];
  scheduled: IUserScheduled;
  sections: ISection[];
  coinSilver: number;
  setShowModal: (v: boolean) => void;
  setShowModalNotEnoughFunds: (v: boolean) => void;
  setLessonRevert: (v: LessonRevertTarget | null) => void;
};

function renderHabitLessonItems(ctx: HabitStripCtx) {
  const {
    lessons,
    scheduled,
    sections,
    coinSilver,
    setShowModal,
    setShowModalNotEnoughFunds,
    setLessonRevert,
  } = ctx;
  return lessons.map((p, ind) => {
    let index = ind;
    if (lessons.length > 7) index = ind - 7;
    let st: "done" | "pass" | "access" | "off" = "off";

    const today = new Date();
    const targetDate = new Date(scheduled.start_date);
    const h = today.getTimezoneOffset() / 60;
    targetDate.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    const daysDifference = Math.floor(
      Math.abs((today.getTime() - targetDate.getTime()) / (1000 * 3600 * 24))
    );

    if (isCompletedLesson(p.Exercise_LessonRelation)) st = "done";
    else if (daysDifference < index) {
      st = "off";
      return (
        <HabitDayPadItem
          title={"День " + p.sequence_number__lesson}
          status={st}
          key={p.id}
        />
      );
    } else if (daysDifference === index) {
      st = "access";
    } else {
      const masDebugInfo: number[] = [];
      let lessonId = false;
      for (let i = 0; i < sections.length; i++) {
        for (let j = 0; j < sections[i].UserLessonsScheduled.length; j++) {
          for (
            let k = 0;
            k < sections[i].UserLessonsScheduled[j].AchievementsRelation.length;
            k++
          ) {
            masDebugInfo.push(
              +sections[i].UserLessonsScheduled[j].AchievementsRelation[k].debugInfo
            );
          }
        }
      }
      masDebugInfo.forEach((item) => {
        if (item === p.id) {
          lessonId = true;
        }
      });
      if (lessonId) {
        return (
          <Link to={"/section/" + p.section_id + "/lesson/" + p.id} key={p.id}>
            <HabitDayPadItem title={"День " + p.sequence_number__lesson} status={"off"} />
          </Link>
        );
      }

      st = "pass";
      const clickPass = () => {
        if (coinSilver < 20) {
          setShowModalNotEnoughFunds(true);
        } else {
          setShowModal(true);
          setLessonRevert({ lessonId: p.id, scheduleId: scheduled.id });
        }
      };
      return (
        <HabitDayPadItem
          title={"День " + p.sequence_number__lesson}
          status={st}
          key={p.id}
          onClick={clickPass}
        />
      );
    }

    return (
      <Link to={"/section/" + p.section_id + "/lesson/" + p.id} key={p.id}>
        <HabitDayPadItem title={"День " + p.sequence_number__lesson} status={st} />
      </Link>
    );
  });
}

interface IProps {
  user: IUser;
  sections: ISection[];
  sectionsLoadedOnce: boolean;
  stories: IStory[];
  reload_lessons: () => void;
  coinSilver: number;
}

export function Main(props: IProps) {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalNotEnoughFunds, setShowModalNotEnoughFunds] =
    useState<boolean>(false);
  const [lessonRevert, setLessonRevert] = useState<LessonRevertTarget | null>(null);
  const pageRef = useRef<HTMLElement | null>(null);
  const dayPadRef = useRef<HTMLDivElement | null>(null);
  const suppressNextClickRef = useRef<boolean>(false);
  const nav = useNavigate();
  const [notifications, setNotifications] = useState<TAppNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeNotification, setActiveNotification] = useState<TAppNotification | null>(null);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const reloadNotifications = () => {
    getNotifications().then((r) => setNotifications(r.items));
  };

  useEffect(() => {
    if (!NOTIFICATIONS_ENABLED) return;
    reloadNotifications();
    const t = setInterval(reloadNotifications, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const pageEl = pageRef.current;
    const dayPadEl = dayPadRef.current;
    if (!pageEl) return;

    const dragState = {
      active: false,
      mode: "vertical" as "vertical" | "horizontal",
      startX: 0,
      startY: 0,
      startScrollTop: 0,
      startScrollLeft: 0,
      moved: false,
    };

    const getScrollablePage = () => {
      // For this layout, outer section has overflow-y, but fallback to window scrolling.
      return pageEl;
    };

    const isInteractive = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return !!el.closest("input, textarea, select, button, a, [role='button'], [contenteditable='true']");
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (isInteractive(e.target)) return;

      const target = e.target as HTMLElement | null;
      const inDayPad = !!(target && dayPadEl && dayPadEl.contains(target));

      dragState.active = true;
      dragState.mode = inDayPad ? "horizontal" : "vertical";
      dragState.startX = e.clientX;
      dragState.startY = e.clientY;
      dragState.startScrollTop = getScrollablePage().scrollTop;
      dragState.startScrollLeft = dayPadEl ? dayPadEl.scrollLeft : 0;
      dragState.moved = false;

      pageEl.style.cursor = "grabbing";
      if (dayPadEl) dayPadEl.style.cursor = "grabbing";
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.active) return;

      const dx = e.clientX - dragState.startX;
      const dy = e.clientY - dragState.startY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        dragState.moved = true;
      }

      if (dragState.mode === "horizontal" && dayPadEl) {
        dayPadEl.scrollLeft = dragState.startScrollLeft - dx;
      } else {
        const scrollable = getScrollablePage();
        scrollable.scrollTop = dragState.startScrollTop - dy;
      }
      e.preventDefault();
    };

    const stopDrag = () => {
      if (!dragState.active) return;
      if (dragState.moved) {
        suppressNextClickRef.current = true;
      }
      dragState.active = false;
      pageEl.style.cursor = "";
      if (dayPadEl) dayPadEl.style.cursor = "";
    };

    pageEl.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);
    pageEl.addEventListener("mouseleave", stopDrag);

    if (dayPadEl) {
      dayPadEl.style.cursor = "grab";
      dayPadEl.style.userSelect = "none";
    }

    return () => {
      pageEl.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDrag);
      pageEl.removeEventListener("mouseleave", stopDrag);
    };
  }, []);

  const onPageClickCapture = (e: React.MouseEvent) => {
    if (!suppressNextClickRef.current) return;
    suppressNextClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  if (props.sections.length === 0 && !props.sectionsLoadedOnce) {
    return (
      <div className="mainPage-lessonsLoading" role="status" aria-live="polite">
        <div className="mainPage-lessonsLoading-spinner" aria-hidden />
        <p className="mainPage-lessonsLoading-text">Загрузка уроков...</p>
      </div>
    );
  }
  if (props.sections.length === 0 && props.sectionsLoadedOnce) {
    return (
      <div className="mainPage-lessonsLoading" role="status" aria-live="polite">
        <p className="mainPage-lessonsLoading-text">
          Не удалось быстро загрузить уроки. Нажмите, чтобы попробовать снова.
        </p>
        <button className="btn btn-fill" onClick={props.reload_lessons}>
          Повторить загрузку
        </button>
      </div>
    );
  }
  const currentScheduled: IUserScheduled =
    getLatestStartedSchedule(props.sections) ?? getCurrentScheduled(props.sections);
  const currentSection: ISection = getCurrentSection(props.sections, currentScheduled);
  /** Для списка "Уроки мудрости" используем единый круг по всей ленте (22 качества). */
  const wisdomRound = Number(currentScheduled?.round ?? 0);
  /** Активный круг именно для текущего модуля (не глобальный round из «чужого» слота). */
  const activeScheduleForCurrent =
    getActiveScheduleForSection(currentSection) ?? currentScheduled;
  const allSectionLessons = Array.isArray(currentSection?.Lesson_SectionRelation)
    ? currentSection.Lesson_SectionRelation
    : [];
  const currentRound = Number(activeScheduleForCurrent?.round ?? currentScheduled?.round ?? 0);
  let currentLessons = allSectionLessons.filter((l) => l.round === currentRound);
  let effectiveRound = currentRound;
  if (currentLessons.length === 0 && allSectionLessons.length > 0) {
    // Fallback: у части пользователей расписание может указывать round,
    // для которого уроки в секции отсутствуют/еще не синхронизированы.
    // Берем ближайший доступный round из уроков, чтобы не показывать пустой трекер.
    const rounds = Array.from(
      new Set(
        allSectionLessons
          .map((l) => Number(l.round))
          .filter((r) => Number.isFinite(r))
      )
    ).sort((a, b) => a - b);
    const fallbackRound =
      rounds.find((r) => r >= currentRound) ??
      rounds[rounds.length - 1];
    if (fallbackRound !== undefined) {
      effectiveRound = fallbackRound;
      currentLessons = allSectionLessons.filter((l) => l.round === fallbackRound);
      logClientDiagnostic({
        action: "habit.round_fallback_used",
        description: "Habit tracker round fallback used",
        endpoint: "/miniapi/get_current_lessons",
        method: "POST",
        userId: props.user?.id || null,
        additionalData: {
          sectionId: currentSection?.id || null,
          scheduledRound: currentRound,
          fallbackRound,
          lessonsInFallbackRound: currentLessons.length,
        },
      });
    }
  }

  const c = prewiusScheduled(props.sections);
  let prewSection: ISection | undefined;
  if (c) {
    prewSection = props.sections.find((s) => s.id === c.SectionId) as ISection;
    const isSameSectionAsCurrent = Number(prewSection?.id) === Number(currentSection?.id);
    if (isSameSectionAsCurrent && prewSection) {
      const previousSectionLessons = prewSection.Lesson_SectionRelation.filter(
        (l) => l.round === c.round
      );
      // Не смешиваем разные модули в day-pad:
      // подтягиваем только хвост незавершенного предыдущего круга
      // в рамках текущего раздела (same section).
      const unfinishedPreviousLessons = previousSectionLessons.filter(
        (lesson) => !isCompletedLesson(lesson.Exercise_LessonRelation)
      );
      if (unfinishedPreviousLessons.length > 0) {
        const currentSequenceSet = new Set(
          currentLessons.map((lesson) => Number(lesson.sequence_number__lesson || 0))
        );
        // Убираем дубли day-pad по номеру дня (sequence):
        // если день уже есть в текущем круге, не подтягиваем его из предыдущего.
        const uniquePreviousTail = unfinishedPreviousLessons.filter(
          (lesson) => !currentSequenceSet.has(Number(lesson.sequence_number__lesson || 0))
        );
        currentLessons = [...uniquePreviousTail, ...currentLessons];
      }
    }
  }
  // Нормализуем порядок для day pad:
  // API может вернуть смесь "предыдущая+текущая ступень" в несортированном виде,
  // из-за чего визуально получается 139,140,134...
  // Сначала дедуп по id, затем строгая сортировка по sequence_number__lesson.
  currentLessons = Array.from(
    new Map(currentLessons.map((lesson) => [lesson.id, lesson])).values()
  ).sort(
    (a, b) =>
      Number(a.sequence_number__lesson || 0) -
      Number(b.sequence_number__lesson || 0)
  );

  /** Вторая полоса day-pad: предыдущий модуль (другая секция), пока тянем «хвост» только внутри одной секции. */
  let previousModuleStripLessons: ILesson[] = [];
  if (c && prewSection && Number(prewSection.id) !== Number(currentSection.id)) {
    const prevRound = Number(c.round ?? 0);
    const allPrev = Array.isArray(prewSection.Lesson_SectionRelation)
      ? prewSection.Lesson_SectionRelation
      : [];
    let prevL = allPrev.filter((l) => l.round === prevRound);
    if (prevL.length === 0 && allPrev.length > 0) {
      const rounds = Array.from(
        new Set(
          allPrev
            .map((l) => Number(l.round))
            .filter((r) => Number.isFinite(r))
        )
      ).sort((a, b) => a - b);
      const fallbackRound =
        rounds.find((r) => r >= prevRound) ?? rounds[rounds.length - 1];
      if (fallbackRound !== undefined) {
        prevL = allPrev.filter((l) => l.round === fallbackRound);
        logClientDiagnostic({
          action: "habit.prev_module_round_fallback",
          description: "Previous module habit strip round fallback",
          endpoint: "/miniapi/get_current_lessons",
          method: "POST",
          userId: props.user?.id || null,
          additionalData: {
            sectionId: prewSection?.id || null,
            scheduledRound: prevRound,
            fallbackRound,
          },
        });
      }
    }
    previousModuleStripLessons = Array.from(
      new Map(prevL.map((lesson) => [lesson.id, lesson])).values()
    ).sort(
      (a, b) =>
        Number(a.sequence_number__lesson || 0) -
        Number(b.sequence_number__lesson || 0)
    );
  }

  const habitStripCtxBase = {
    sections: props.sections,
    coinSilver: props.coinSilver,
    setShowModal,
    setShowModalNotEnoughFunds,
    setLessonRevert,
  };
  /** В "Уроки мудрости" показываем ровно 22 качества, без дублей секций. */
  const wisdomSections = Array.from(
    new Map(
      [...(props.sections || [])]
        .sort((a, b) => {
          const sa = Number(a.sequence_number__section || 0);
          const sb = Number(b.sequence_number__section || 0);
          if (sa === sb) return Number(a.id || 0) - Number(b.id || 0);
          return sa - sb;
        })
        .map((section) => [Number(section.sequence_number__section || 0), section])
    ).values()
  ).slice(0, 22);

  let src: string;
  if (props.user.avatarLink !== "no-avatar") {
    src = props.user.avatarLink;
  } else {
    src = "./avatar.png";
  }

  return (
    <section
      className="section mainPage mainPage-safeTop flex flex-column"
      ref={pageRef}
      onClickCapture={onPageClickCapture}
    >
      {/* Wiget */}
      <div className="mainPage_wiget flex flex-row">
        <img
          src={src}
          className="mainPage_wiget_icon__img"
          alt="аватарка на главной странице"
        />
        <div className="mainPage_wiget_textContent">
          <div className="mainPage_wiget_textContent__greeting flex flex-row">
            Привет, {props.user.name}!
          </div>
          <div className="mainPage_wiget_textContent__description">
            Самое время начать учиться
          </div>
        </div>
        {NOTIFICATIONS_ENABLED && (
          <button
            type="button"
            className="mainPage_notifyBell"
            onClick={() => setShowNotifications((p) => !p)}
            aria-label="Уведомления"
          >
            <img src={bell_icon} alt="" className="mainPage_notifyBellIcon" />
            {unreadCount > 0 && <span className="mainPage_notifyBadge">{unreadCount}</span>}
          </button>
        )}
      </div>
      {NOTIFICATIONS_ENABLED && showNotifications && (
        <div className="mainPage_notifyDropdown">
          <div className="mainPage_notifyHeader flex flex-row">
            <span>Уведомления</span>
            <button
              type="button"
              className="mainPage_notifyReadAll"
              onClick={() => {
                markAllNotificationsRead().then(reloadNotifications);
              }}
            >
              Прочитать все
            </button>
          </div>
          <div className="mainPage_notifyList">
            {notifications.length === 0 && (
              <div className="mainPage_notifyEmpty">Пока уведомлений нет</div>
            )}
            {notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`mainPage_notifyItem ${n.isRead ? "" : "mainPage_notifyItem-unread"}`}
                onClick={() => {
                  setActiveNotification(n);
                  if (!n.isRead) {
                    markNotificationRead(n.id).then(reloadNotifications);
                  }
                }}
              >
                {n.imageUrl ? (
                  <img src={n.imageUrl} className="mainPage_notifyPreview" alt="" />
                ) : (
                  <span className="mainPage_notifyPreview mainPage_notifyPreview-empty">•</span>
                )}
                <span className="mainPage_notifyTitle">{n.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Story Line */}
      <div className="mainPage_storyLine-box">
        <div className="mainPage_storyLine flex flex-row">
          {(Array.isArray(props.stories) ? props.stories : []).map((story, index) => {
            return (
              <Link to={"/story/" + story.id} key={index}>
                <StoryLineItem
                  title={story.title}
                  url={story.url}
                  border={story.StorysViewedRelation.length === 0}
                />
              </Link>
            );
          })}
        </div>
      </div>
      {/* Habit trecker */}
      <div className="mainPage_habit">
        <div className="mainPage_habit_header">Формирование привычки</div>
        <div className="mainPage_habit_dayPad" ref={dayPadRef}>
          {currentLessons.length === 0 && previousModuleStripLessons.length === 0 ? (
            <div className="mainPage-habitEmpty">
              Нет доступных уроков для текущего круга. Обновите страницу чуть позже.
            </div>
          ) : (
            <>
              {previousModuleStripLessons.length > 0 && c && (
                <div className="mainPage_habit_dayPad-moduleTrack" data-module="previous">
                  {renderHabitLessonItems({
                    ...habitStripCtxBase,
                    lessons: previousModuleStripLessons,
                    scheduled: c,
                  })}
                </div>
              )}
              {previousModuleStripLessons.length > 0 && currentLessons.length > 0 && (
                <div className="mainPage_habit_dayPad-moduleDivider" aria-hidden />
              )}
              {currentLessons.length > 0 && (
                <div className="mainPage_habit_dayPad-moduleTrack" data-module="current">
                  {renderHabitLessonItems({
                    ...habitStripCtxBase,
                    lessons: currentLessons,
                    scheduled: currentScheduled,
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Lessons Wiget */}
      <div className="mainPage_lessonWisdom">
        <div className="mainPage_lessonWisdom_header">Уроки мудрости</div>
        <div className="mainPage_lessonWisdom_Pad">
          <div className="mainPage_lessonWisdom_Pad_leftColumn flex flex-column">
            {wisdomSections
              .map((section, ind) => {
                if (ind % 2 === 1) return false;
                const sectionRound = wisdomRound;
                /** В БД round уже 1-based (1,2,3); подпись совпадает с кругом. */
                const sectionRoundLabel = Number(sectionRound) || 1;
                let { complitedLesson, lockedSection } =
                  calculateComplitedLesson(section, sectionRound, props.sections);
                const gold = hasGold(section);
                const pearl = hasPearl(section);

                return (
                  <LessonWidget
                    index={section.sequence_number__section}
                    title={section.title}
                    step={"Ступень " + sectionRoundLabel}
                    totalLesson={7}
                    complitedLesson={complitedLesson}
                    lockedSection={lockedSection}
                    gold={gold}
                    pearl={pearl}
                    key={ind}
                    preivSection={prewSection?.id === section.id}
                  />
                );
              })
              .filter((w) => {
                return w !== false;
              })}
          </div>
          <div className="mainPage_lessonWisdom_Pad_rightColumn flex flex-column">
            {wisdomSections
              .map((section, ind) => {
                if (ind % 2 === 0) return false;
                const sectionRound = wisdomRound;
                /** В БД round уже 1-based (1,2,3); подпись совпадает с кругом. */
                const sectionRoundLabel = Number(sectionRound) || 1;
                let { complitedLesson, lockedSection } =
                  calculateComplitedLesson(section, sectionRound, props.sections);
                const gold = hasGold(section);
                const pearl = hasPearl(section);

                return (
                  <LessonWidget
                    index={section.sequence_number__section}
                    title={section.title}
                    step={"Ступень " + sectionRoundLabel}
                    totalLesson={7}
                    complitedLesson={complitedLesson}
                    lockedSection={lockedSection}
                    gold={gold}
                    pearl={pearl}
                    key={ind}
                    preivSection={prewSection?.id === section.id}
                  />
                );
              })
              .filter((w) => {
                return w !== false;
              })}
          </div>
        </div>
      </div>
      {/* Footer Menu */}
      <div className="profile_footer">
        <Link to="/rating">
          <div className="footer_tab">
            <img src={lighting} alt="Комьюнити" />
            <span className="textMenu">Комьюнити</span>
          </div>
        </Link>
        <Link to="/">
          <div className="footer_tab">
            <img src={application} alt="Главная" />
            <span className="textMenu">Главная</span>
          </div>
        </Link>
        <Link to="/profile">
          <div className="footer_tab">
            <img src={profile_icon} alt="Профиль" />
            <span className="textMenu">Профиль</span>
          </div>
        </Link>
      </div>
      {showModal && (
        <Modal
          text={
            "Мы скучали по тебе! Немного отдохнуть - это нормально. Хочешь продлить доступ пропущенному уроку?"
          }
          onClickLeft={() => {
            if (!lessonRevert) return;
            const less = findLessonById(props.sections, lessonRevert.lessonId);
            if (!less) return;
            withDrawalSilver(lessonRevert.scheduleId, less.id).then(() => {
              props.reload_lessons();
              setShowModal(false);
              setLessonRevert(null);
              nav("/section/" + less.section_id + "/lesson/" + less.id);
            });
          }}
          onClickRight={() => {
            setShowModal(false);
            setLessonRevert(null);
          }}
          textBtnLeft={"Продлить"}
          textBtnRight={"Пропустить"}
          prise={true}
        />
      )}
      {showModalNotEnoughFunds && (
        <Modal
          text={"У вас недостаточно монет"}
          onClickRight={() => {
            setShowModalNotEnoughFunds(false);
          }}
          textBtnLeft={""}
          textBtnRight={"Ok"}
          prise={true}
        />
      )}
      {NOTIFICATIONS_ENABLED && activeNotification && (
        <section className="mainPage_notifyModal">
          <div className="mainPage_notifyModalCard">
            <h3>{activeNotification.title}</h3>
            {activeNotification.imageUrl && (
              <img src={activeNotification.imageUrl} alt="" className="mainPage_notifyModalImage" />
            )}
            <p>{activeNotification.body || "Без текста"}</p>
            <button
              type="button"
              className="mainPage_notifyModalClose"
              onClick={() => setActiveNotification(null)}
            >
              Закрыть
            </button>
          </div>
        </section>
      )}
    </section>
  );
}
